// frontend/src/hooks/useDutyOperators.ts
// Версия: 1.0.0 (2026-09-10)
// Список ОД из реестра БД (API) с кэшем на время сессии; при недоступности API — резервный OPERATORS.

import { useEffect, useState } from 'react';
import { OPERATORS, Operator } from '../constants/operators.const';
import { fetchDutyOperators, toOperator } from '../api/dutyOperatorsApi';

export type DutyOperatorsSource = 'api' | 'fallback';

export interface DutyOperatorsState {
  operators: Operator[];
  loading: boolean;
  error: string | null;
  source: DutyOperatorsSource;
  version: string;
}

interface DutyOperatorsCache {
  operators: Operator[];
  version: string;
  source: DutyOperatorsSource;
}

const FALLBACK_MESSAGE = 'Реестр дежурных недоступен — показан резервный список';

let cache: DutyOperatorsCache | null = null;
let inflight: Promise<DutyOperatorsCache> | null = null;

async function loadDutyOperators(): Promise<DutyOperatorsCache> {
  if (cache) return cache;
  if (!inflight) {
    inflight = fetchDutyOperators()
      .then((data) => {
        const operators = (data.operators || []).filter((r) => r.isActive).map(toOperator);
        if (operators.length === 0) {
          throw new Error('duty-operators: пустой список');
        }
        cache = { operators, version: data.version, source: 'api' };
        return cache;
      })
      .catch((err) => {
        console.warn('[useDutyOperators] API недоступен, используется резервный список', err);
        const fallback: DutyOperatorsCache = {
          operators: OPERATORS,
          version: 'fallback',
          source: 'fallback',
        };
        return fallback;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** Сброс кэша (например, после изменения реестра администратором) */
export function resetDutyOperatorsCache(): void {
  cache = null;
}

export function useDutyOperators(): DutyOperatorsState {
  const [state, setState] = useState<DutyOperatorsState>(() =>
    cache
      ? {
          operators: cache.operators,
          loading: false,
          error: null,
          source: cache.source,
          version: cache.version,
        }
      : { operators: [], loading: true, error: null, source: 'api', version: '' }
  );

  useEffect(() => {
    let cancelled = false;
    loadDutyOperators().then((result) => {
      if (cancelled) return;
      setState({
        operators: result.operators,
        loading: false,
        error: result.source === 'fallback' ? FALLBACK_MESSAGE : null,
        source: result.source,
        version: result.version,
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
